import type { PrinterWithRelations } from '@maker-toolchest/shared';
import { Box, Zap, Layers, Settings, FileText } from 'lucide-react';

interface PrinterLibraryDetailViewProps {
  printer: PrinterWithRelations;
}

interface SpecRowProps {
  label: string;
  value: string | number | null | undefined;
  unit?: string;
}

function SpecRow({ label, value, unit }: SpecRowProps) {
  if (value == null) return null;

  return (
    <div className="flex items-center justify-between border-b border-border py-2 last:border-b-0">
      <span className="text-sm text-foreground-muted">{label}</span>
      <span className="text-sm font-medium text-foreground">
        {value}{unit ? ` ${unit}` : ''}
      </span>
    </div>
  );
}

interface SpecSectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}

function SpecSection({ title, icon, children }: SpecSectionProps) {
  return (
    <div className="rounded-lg border border-border bg-background">
      <div className="flex items-center gap-2 border-b border-border bg-background-subtle px-4 py-3">
        <div className="text-primary">{icon}</div>
        <h3 className="font-semibold text-foreground">{title}</h3>
      </div>
      <div className="divide-y divide-border px-4">
        {children}
      </div>
    </div>
  );
}

export function PrinterLibraryDetailView({ printer }: PrinterLibraryDetailViewProps) {
  const hasBuildVolume = printer.buildVolumeX || printer.buildVolumeY || printer.buildVolumeZ;
  const hasTemperature = printer.nozzleTempMax || printer.bedTempMax || printer.chamberTempMax;
  const hasSpeed = printer.maxPrintSpeed || printer.maxTravelSpeed;

  // Technology-specific checks
  const hasResinSpecs = printer.technology === 'SLA' && (
    printer.xyResolution ||
    printer.screenResolution ||
    printer.lightSourceType ||
    printer.lightWavelength ||
    printer.zLayerHeightMin ||
    printer.zLayerHeightMax ||
    printer.liftSpeed ||
    printer.resinVatCapacity
  );

  const hasPowderSpecs = (printer.technology === 'SLS' || printer.technology === 'MJF') && (
    printer.laserPower ||
    printer.laserSpotSize ||
    printer.zLayerHeightMin ||
    printer.zLayerHeightMax ||
    printer.powderCapacity ||
    printer.cooldownTime
  );

  const hasBinderJetSpecs = printer.technology === 'BINDER_JET' && (
    printer.zLayerHeightMin ||
    printer.zLayerHeightMax ||
    printer.powderCapacity ||
    printer.cooldownTime
  );

  return (
    <div className="p-6">
      <div className="mx-auto max-w-4xl space-y-6">

        {/* Build Volume */}
        {hasBuildVolume && (
          <SpecSection title="Build Volume" icon={<Box className="h-5 w-5" />}>
            <SpecRow label="Width (X)" value={printer.buildVolumeX} unit="mm" />
            <SpecRow label="Depth (Y)" value={printer.buildVolumeY} unit="mm" />
            <SpecRow label="Height (Z)" value={printer.buildVolumeZ} unit="mm" />
          </SpecSection>
        )}

        {/* Temperature Capabilities (FDM) */}
        {hasTemperature && (
          <SpecSection title="Temperature Capabilities" icon={<Zap className="h-5 w-5" />}>
            <SpecRow label="Max Nozzle Temperature" value={printer.nozzleTempMax} unit="°C" />
            <SpecRow label="Max Bed Temperature" value={printer.bedTempMax} unit="°C" />
            <SpecRow label="Max Chamber Temperature" value={printer.chamberTempMax} unit="°C" />
          </SpecSection>
        )}

        {/* Speed Capabilities (FDM) */}
        {hasSpeed && (
          <SpecSection title="Speed Capabilities" icon={<Zap className="h-5 w-5" />}>
            <SpecRow label="Max Print Speed" value={printer.maxPrintSpeed} unit="mm/s" />
            <SpecRow label="Max Travel Speed" value={printer.maxTravelSpeed} unit="mm/s" />
          </SpecSection>
        )}

        {/* Resin Printer Specifications */}
        {hasResinSpecs && (
          <SpecSection title="Resin Printer Specifications" icon={<Layers className="h-5 w-5" />}>
            <SpecRow label="XY Resolution" value={printer.xyResolution} unit="µm" />
            <SpecRow label="Screen Resolution" value={printer.screenResolution} />
            <SpecRow label="Light Source Type" value={printer.lightSourceType} />
            <SpecRow label="Light Wavelength" value={printer.lightWavelength} unit="nm" />
            <SpecRow label="Min Layer Height" value={printer.zLayerHeightMin} unit="µm" />
            <SpecRow label="Max Layer Height" value={printer.zLayerHeightMax} unit="µm" />
            <SpecRow label="Lift Speed" value={printer.liftSpeed} unit="mm/min" />
            <SpecRow label="Resin Vat Capacity" value={printer.resinVatCapacity} unit="ml" />
          </SpecSection>
        )}

        {/* Powder Printer Specifications (SLS/MJF) */}
        {hasPowderSpecs && (
          <SpecSection title="Powder Printer Specifications" icon={<Layers className="h-5 w-5" />}>
            <SpecRow label="Laser Power" value={printer.laserPower} unit="W" />
            <SpecRow label="Laser Spot Size" value={printer.laserSpotSize} unit="µm" />
            <SpecRow label="Min Layer Height" value={printer.zLayerHeightMin} unit="µm" />
            <SpecRow label="Max Layer Height" value={printer.zLayerHeightMax} unit="µm" />
            <SpecRow label="Powder Capacity" value={printer.powderCapacity} unit="L" />
            <SpecRow label="Cooldown Time" value={printer.cooldownTime} unit="hours" />
          </SpecSection>
        )}

        {/* Binder Jet Specifications */}
        {hasBinderJetSpecs && (
          <SpecSection title="Binder Jet Specifications" icon={<Layers className="h-5 w-5" />}>
            <SpecRow label="Layer Height" value={printer.zLayerHeightMin === printer.zLayerHeightMax ? printer.zLayerHeightMin : null} unit="µm" />
            {printer.zLayerHeightMin !== printer.zLayerHeightMax && (
              <>
                <SpecRow label="Min Layer Height" value={printer.zLayerHeightMin} unit="µm" />
                <SpecRow label="Max Layer Height" value={printer.zLayerHeightMax} unit="µm" />
              </>
            )}
            <SpecRow label="Powder Capacity" value={printer.powderCapacity} unit="L" />
            <SpecRow label="Cooldown Time" value={printer.cooldownTime} unit="hours" />
          </SpecSection>
        )}

        {/* Slot Configuration */}
        <SpecSection title="Slot Configuration" icon={<Settings className="h-5 w-5" />}>
          <SpecRow label="Default Nozzle Slots" value={printer.defaultNozzleSlots} />
          <SpecRow label="Default Hotend Slots" value={printer.defaultHotendSlots} />
          <SpecRow label="Default Spool Slots" value={printer.defaultSpoolSlots} />
          <SpecRow label="Default Accessory Slots" value={printer.defaultAccessorySlots} />
        </SpecSection>

        {/* Notes */}
        {printer.notes && (
          <SpecSection title="Notes" icon={<FileText className="h-5 w-5" />}>
            <div className="py-3">
              <p className="whitespace-pre-wrap text-sm text-foreground">{printer.notes}</p>
            </div>
          </SpecSection>
        )}
      </div>
    </div>
  );
}
